import React, { useState } from 'react';
import {
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Typography, 
    Box, 
    Paper,
    Chip,
    TextField,
    MenuItem
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChooseQuestionDialog from './ChooseQuestionDialog';

const useStyles = makeStyles(theme => ({
    root: {
        minWidth: 300,
        maxWidth: 380
    },
    title: {
        fontFamily: 'Assistant',        
        color: 'white',
        backgroundColor: theme.palette.primary[700],
        height: '24px',
        '& > .MuiTypography-root': {
            fontSize: '12px',
            fontWeight: 500,
        }
    },
    section: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(3),
        border: '1px solid #e0e0e0',
        borderRadius: 8
    },
    sectionTitle: {
        fontFamily: 'Assistant',
        fontWeight: 600,
        fontSize: '14px',
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main
    },
    sectionDescription: {
        fontFamily: 'Assistant',
        fontSize: '12px',
        color: '#666',
        marginBottom: theme.spacing(2)
    },
    selectedQuestion: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5),
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        marginBottom: theme.spacing(0.5)
    },
    selectButton: {
        textTransform: 'none',
        fontFamily: 'Assistant',
        fontSize: '10px',
        padding: theme.spacing(0.5, 1)
    },
    requiredMark: {
        color: 'red',
        marginLeft: 4
    },
    dialogActions: {
        padding: theme.spacing(1),
        justifyContent: 'space-between'
    },
    routingOrderSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: '#f9f9f9',
        borderRadius: 4
    },
    routingOrderField: {
        marginTop: theme.spacing(1),
        '& .MuiInputBase-root': {
            fontFamily: 'Assistant',
            fontSize: '14px'
        }
    }
}));

export default function InPersonSignatureDialog({ open, onClose, onSave }) {
    const classes = useStyles();
    const [hostEmailQuestion, setHostEmailQuestion] = useState(null);
    const [hostNameQuestion, setHostNameQuestion] = useState(null);
    const [signerEmailQuestion, setSignerEmailQuestion] = useState(null);
    const [routingOrder, setRoutingOrder] = useState('1');
    const [activeDialog, setActiveDialog] = useState(null);

    const isValid = hostEmailQuestion && signerEmailQuestion;

    const handleSave = () => {
        if (isValid) {
            onSave({
                hostEmail: hostEmailQuestion,
                hostName: hostNameQuestion,
                signerEmail: signerEmailQuestion,
                routingOrder: routingOrder
            });
        }
    };

    const handleQuestionSelected = (question, parentQuestion, type) => {
        switch (type) {
            case 'hostEmail':
                setHostEmailQuestion({ question, parentQuestion });
                break;
            case 'hostName':
                setHostNameQuestion({ question, parentQuestion });
                break;
            case 'signerEmail':
                setSignerEmailQuestion({ question, parentQuestion });
                break;
        }
        setActiveDialog(null);
    };

    const renderQuestionSection = (title, description, selectedQuestion, onSelect, required = false) => (
        <Paper className={classes.section}>
            <Typography className={classes.sectionTitle}>
                {title}
                {required && <span className={classes.requiredMark}>*</span>}
            </Typography>
            <Typography className={classes.sectionDescription}>
                {description}
            </Typography>
            {selectedQuestion ? (
                <Box className={classes.selectedQuestion}>
                    <Chip 
                        label={`${selectedQuestion.parentQuestion ? selectedQuestion.parentQuestion.title + ' - ' : ''}${selectedQuestion.question.title || selectedQuestion.question.text}`}
                        onDelete={() => onSelect(null)}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                </Box>
            ) : (
                <Button 
                    variant="outlined" 
                    onClick={() => setActiveDialog(title)}
                    className={classes.selectButton}
                >
                    Select Question
                </Button>
            )}
        </Paper>
    );

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose} 
                className={classes.root}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className={classes.title}>
                    In-Person Signature Setup
                </DialogTitle>
                <DialogContent>
                    <Box>
                        {renderQuestionSection(
                            'Host Email',
                            'Select question that contains the email of the person who will host the signing session (must be present during signing)',
                            hostEmailQuestion,
                            setHostEmailQuestion,
                            true
                        )}
                        
                        {renderQuestionSection(
                            'Host Name',
                            'Select question that contains the full name of the host (optional - will use host email if not provided)',
                            hostNameQuestion,
                            setHostNameQuestion,
                            false
                        )}
                        
                        {renderQuestionSection(
                            'Signer Email',
                            'Select question that contains the email of the person who will actually sign the document',
                            signerEmailQuestion,
                            setSignerEmailQuestion,
                            true
                        )}
                        
                        <Box className={classes.routingOrderSection}>
                            <Typography className={classes.sectionTitle}>
                                Signing Order
                            </Typography>
                            <Typography className={classes.sectionDescription}>
                                Set when this signer should sign (lower numbers sign first, same numbers sign in parallel)
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                value={routingOrder}
                                onChange={(e) => setRoutingOrder(e.target.value)}
                                className={classes.routingOrderField}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value="1">1 - First to sign</MenuItem>
                                <MenuItem value="2">2 - Second to sign</MenuItem>
                                <MenuItem value="3">3 - Third to sign</MenuItem>
                                <MenuItem value="4">4 - Fourth to sign</MenuItem>
                                <MenuItem value="5">5 - Fifth to sign</MenuItem>
                            </TextField>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={onClose} size="small">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        color="primary" 
                        variant="contained"
                        disabled={!isValid}
                        size="small"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ChooseQuestionDialog
                open={activeDialog === 'Host Email'}
                onClose={() => setActiveDialog(null)}
                onQuestionSelected={(question, parentQuestion) => 
                    handleQuestionSelected(question, parentQuestion, 'hostEmail')
                }
                filter={q => q.type === 'email'}
            />
            
            <ChooseQuestionDialog
                open={activeDialog === 'Host Name'}
                onClose={() => setActiveDialog(null)}
                onQuestionSelected={(question, parentQuestion) => 
                    handleQuestionSelected(question, parentQuestion, 'hostName')
                }
                filter={q => q.type === 'text_box'}
            />
            
            <ChooseQuestionDialog
                open={activeDialog === 'Signer Email'}
                onClose={() => setActiveDialog(null)}
                onQuestionSelected={(question, parentQuestion) => 
                    handleQuestionSelected(question, parentQuestion, 'signerEmail')
                }
                filter={q => q.type === 'email'}
            />
        </>
    );
} 